import type { AdminMessage } from '../../api/types.js';
import { isMediaMessage, isMiniProgramMessage, messageMediaUrl, renderWechatEmojis, shouldRenderMessageText } from '../../lib/utils.js';

interface MessageMediaProps {
  message: AdminMessage;
}

export function MessageMedia({ message }: MessageMediaProps) {
  if (isMiniProgramMessage(message)) {
    return <MiniProgramCard message={message} />;
  }
  if (!isMediaMessage(message)) return null;
  const url = messageMediaUrl(message);
  if (!url) return null;

  if (message.mediaType === 'voice' || message.msgType === 34) {
    return (
      <div className="message-media audio">
        <audio controls preload="metadata" src={url} />
        <Transcript message={message} />
      </div>
    );
  }

  if (message.mediaType === 'video' || message.msgType === 43) {
    return (
      <div className="message-media video">
        <video controls preload="metadata" src={url} />
        <Transcript message={message} />
      </div>
    );
  }

  const alt = message.content && message.content !== '[图片]' ? message.content : '聊天图片';
  return (
    <a className="message-image-link" href={url} target="_blank" rel="noopener noreferrer">
      <img className="message-image" src={url} alt={alt} loading="lazy" />
    </a>
  );
}

function MiniProgramCard({ message }: { message: AdminMessage }) {
  const metadata = (message.mediaMetadata ?? {}) as Record<string, unknown>;
  const title = stringValue(metadata.title) ?? message.content.replace(/^\[小程序\]\s*/, '') ?? '小程序';
  const description = stringValue(metadata.description) ?? stringValue(metadata.sourceDisplayName) ?? '小程序卡片';
  const thumbUrl = stringValue(metadata.thumbUrl);
  const pagePath = stringValue(metadata.pagePath) ?? stringValue(metadata.pagepath);

  return (
    <div className="message-mini-program">
      <div className="mini-program-label">小程序</div>
      <div className="mini-program-body">
        <div className="mini-program-copy">
          <div className="mini-program-title">{title}</div>
          <div className="mini-program-desc">{description}</div>
          {pagePath ? <div className="mini-program-path">{pagePath}</div> : null}
        </div>
        {thumbUrl ? <img className="mini-program-thumb" src={thumbUrl} alt={title} loading="lazy" /> : null}
      </div>
    </div>
  );
}

function Transcript({ message }: { message: AdminMessage }) {
  if (message.mediaTranscript) {
    return (
      <div
        className="media-transcript"
        dangerouslySetInnerHTML={{ __html: renderWechatEmojis(message.mediaTranscript) }}
      />
    );
  }
  const metadata = (message.mediaMetadata || {}) as Record<string, unknown>;
  if (metadata.asrError) {
    return <div className="media-transcript muted">转写失败</div>;
  }
  if (
    message.mediaType === 'voice' ||
    message.mediaType === 'video' ||
    message.msgType === 34 ||
    message.msgType === 43
  ) {
    return <div className="media-transcript muted">转写处理中</div>;
  }
  return null;
}

export function MessageText({ message }: { message: AdminMessage }) {
  const hasMedia = isMediaMessage(message);
  const hasMiniProgram = isMiniProgramMessage(message);
  if (!shouldRenderMessageText(message.content, hasMedia, hasMiniProgram)) return null;
  return (
    <div
      className="message-text"
      dangerouslySetInnerHTML={{ __html: renderWechatEmojis(message.content || '') }}
    />
  );
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
